import {
  MediaConfigOptions,
  VideoCoreOptions
} from '../mediaPlayback.types';

/**
 * getMediaConfig()
 *  Utility function to build `mediaConfig` value to be passed into `createPlayer()`
 *
 * @param playerOptMediaConfig -  `mediaConfig` key passed into `playerOptions` prop
 *  Serves as default value
 * @param mediaConfig -           prop passed into `MediaPlayback` component
 * @param advertisingConfig -     prop passed into `MediaPlayback` component
 * @param controlsConfig -        prop passed into `MediaPlayback` component
 * @param pluginConfigs -         prop passed into `MediaPlayback` component
 * @param trackingConfig -        prop passed into `MediaPlayback` component
 */
export const getMediaConfig: Function = (
  playerOptMediaConfig: MediaConfigOptions,
  mediaConfig: MediaConfigOptions,
  advertisingConfig: object,
  controlsConfig: object,
  pluginConfigs: object[],
  trackingConfig: object
): MediaConfigOptions => {

  /**
   * Handle videocore object separately
   * - only need to override keys 'advertising' and 'tracking' as needed
   * - maintain remaining options
   **/
  const {
    videocore,
    ...otherMCOptions
  }: MediaConfigOptions = mediaConfig;
  
  // Set initial videocore value passed in via mediaConfig
  let finalVideoCore: VideoCoreOptions = { ...videocore };

  // Set initial media config to value passed in via playerOptions
  let finalMediaConfig: MediaConfigOptions = playerOptMediaConfig;

  // Allow non-videocore mediaConfig values to override playerOptions.mediaConfig
  finalMediaConfig = { ...finalMediaConfig, ...otherMCOptions };

  /**
   * `advertisingConfig` (prop)
   * - overrides `advertising` in `finalVideoCore` if explicitly passed in
   * - else if key `advertising` exists in `mediaConfig.videocore`, override in `finalVideoCore`
   */
  if (advertisingConfig !== undefined) {
    finalVideoCore = { ...finalVideoCore, advertising: advertisingConfig };
  }

  /**
   * `trackingConfig` (prop)
   * - overrides `tracking` in `finalVideoCore` if explicitly passed in
   * - else if key `tracking` exists in `mediaConfig.videocore`, override in `finalVideoCore`
   */
  if (trackingConfig !== undefined) {
    finalVideoCore = { ...finalVideoCore, tracking: trackingConfig };
  }

  // Override `finalMediaConfig.videocore` if `finalVideoCore` is non-empty
  if (Object.keys(finalVideoCore).length > 0) {
    finalMediaConfig = {
      ...finalMediaConfig,
      videocore: { ...finalMediaConfig.videocore, ...finalVideoCore }
    };
  }

  // Override `controlsConfig` if explictly passed in
  if (controlsConfig !== undefined) {
    finalMediaConfig.controlsConfig = controlsConfig;
  }

  // Override `pluginConfigs` if explicitly passed in
  if (pluginConfigs !== undefined) {
    finalMediaConfig.pluginConfigs = pluginConfigs;
  }

  return finalMediaConfig;

};

export default {
  getMediaConfig
};
